import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import RegisterPage from "./pages/RegisterPage";
import Dashboard from "./pages/Dashboard";
import Today from "./pages/Today";
import CasesList from "./pages/CasesList";
import Correspondence from "./pages/Correspondence";
import CaseDetail from "./pages/CaseDetail";
import ImportCases from "./pages/ImportCases";
import CalendarView from "./pages/CalendarView";
import AdminPanel from "./pages/AdminPanel";
import PretrialManagement from "./pages/PretrialManagement";
import Reports from "./pages/Reports";
import Messages from "./pages/Messages";
import Statistics from "./pages/Statistics";
import Hearings from "./pages/Hearings";
import BenchCalendar from "./pages/BenchCalendar";
import AssignmentBoard from "./pages/AssignmentBoard";
import KpolD from "./pages/KpolD";
import Dispatch from "./pages/Dispatch";
import Printouts from "./pages/Printouts";
import JudgeCases from "./pages/JudgeCases";
import HearingCalculator from "./pages/HearingCalculator";
import JudgeReviews from "./pages/JudgeReviews";
import ExportsHistory from "./pages/ExportsHistory";
import FilingsSearch from "./pages/FilingsSearch";
import Dockets from "./pages/Dockets";
import MyCompositions from "./pages/MyCompositions";
import MyTemplates from "./pages/MyTemplates";
import Layout from "./components/Layout";
import { isScopedRole } from "./components/RoleScopeBanner";
import { RoleChooser } from "./components/RoleChooser";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Privacy mode helper - anonymize names showing only first 2 chars
export const anonymize = (text) => {
  if (!text) return text;
  return text.split(/([,\s]+)/).map(part => {
    if (part.trim().length <= 2 || /^[,\s]+$/.test(part)) return part;
    return part.substring(0, 2) + "**";
  }).join("");
};

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Axios global config - timeout 120s for large datasets
axios.defaults.timeout = 120000;

// Axios interceptor for auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Prevent multiple 401 redirects
let isRedirecting = false;

axios.interceptors.response.use(
  (response) => {
    // iter94: προειδοποιήσεις (μη-αποκλειστικοί κανόνες) από το backend → toast
    const w = response?.data?.warnings;
    if (Array.isArray(w) && w.length) toast.warning(w.join("\n"), { duration: 15000 });
    const xw = response?.headers?.["x-warnings"];
    if (xw) { try { toast.warning(decodeURIComponent(xw).split(" | ").join("\n"), { duration: 15000 }); } catch { /* ignore */ } }
    return response;
  },
  async (error) => {
    // Retry on network/timeout errors (once)
    const config = error.config;
    if (!config._retry && (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' || !error.response)) {
      config._retry = true;
      config.timeout = 180000;
      return axios(config);
    }
    if (error.response?.status === 401 && !isRedirecting) {
      const isLoginPage = window.location.pathname === "/login";
      if (!isLoginPage) {
        isRedirecting = true;
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Φόρτωση...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user } = useAuth();

  if (user?.role !== "admin") {
    toast.error("Δεν έχετε δικαιώματα πρόσβασης");
    return <Navigate to="/" replace />;
  }

  return children;
};

// Smart default page based on user role & allowed_pages
const DefaultPage = () => {
  const { user } = useAuth();

  // Judges → first judge page
  if (user?.role === "judge") {
    return <Navigate to="/judge-cases" replace />;
  }
  if (isScopedRole(user)) {
    return <Navigate to="/hearings" replace />;
  }

  // Users with restricted allowed_pages that don't include dashboard
  const allowed = user?.allowed_pages;
  if (allowed && !allowed.includes("dashboard")) {
    const pageRouteMap = {
      "today": "/today", "cases": "/cases", "hearings": "/hearings", "bench-calendar": "/bench-calendar", "assignment-board": "/assignment-board",
      "hearing-calculator": "/hearing-calculator", "kpold": "/kpold",
      "pretrial": "/pretrial", "dispatch": "/dispatch", "printouts": "/printouts",
      "import": "/import", "calendar": "/calendar", "reports": "/reports",
      "statistics": "/statistics", "correspondence": "/correspondence",
      "messages": "/messages", "judge-cases": "/judge-cases", "judge-reviews": "/judge-reviews",
    };
    const firstAllowed = allowed.find(p => pageRouteMap[p]);
    if (firstAllowed) {
      return <Navigate to={pageRouteMap[firstAllowed]} replace />;
    }
  }

  // Admin or full access → Dashboard
  return <Dashboard />;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleChooser, setRoleChooser] = useState({ open: false, busy: false });

  const applyAuth = (data) => {
    isRedirecting = false;
    localStorage.setItem("token", data.access_token);
    localStorage.setItem("user", JSON.stringify(data.user));
    setUser(data.user);
  };

  const selectRole = async (role) => {
    try {
      setRoleChooser(s => ({ ...s, busy: true }));
      const r = await axios.post(`${API}/auth/select-role`, { role });
      applyAuth(r.data);
      setRoleChooser({ open: false, busy: false });
      toast.success(`Ιδιότητα: ${r.data.user.role}`);
      window.location.href = "/";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα αλλαγής ιδιότητας");
      setRoleChooser(s => ({ ...s, busy: false }));
    }
  };
  const openRoleChooser = () => setRoleChooser({ open: true, busy: false });

  useEffect(() => {
    // Τίτλος καρτέλας = το domain της εγκατάστασης (όχι «Emergent | Fullstack App»)
    const host = window.location.hostname;
    if (host) document.title = host;
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");
      
      if (token && storedUser) {
        try {
          // Verify token is still valid
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          setUser(null);
        }
      }
      setLoading(false);
    };

    // Initialize system
    axios.post(`${API}/init`).catch(() => {});
    initAuth();
  }, []);

  // RCA (πρωινά Cloudflare 52x): όσο οι replicas «ζεσταίνονται» το edge επιστρέφει HTML σφάλματος → αυτόματη επανάληψη
  const postLoginWithRetry = async (username, password, attempts = 4) => {
    let lastErr;
    for (let i = 0; i < attempts; i++) {
      try {
        return await axios.post(`${API}/auth/login`, { username, password }, { timeout: 20000 });
      } catch (error) {
        lastErr = error;
        const status = error.response?.status;
        const transient = !error.response || status >= 500 || error.code === "ECONNABORTED";
        if (!transient || i === attempts - 1) throw error;
        toast.message("Ο διακομιστής ξεκινά…", { description: `Αυτόματη επανάληψη σύνδεσης (${i + 1}/${attempts - 1}). Παρακαλώ περιμένετε.`, duration: 4000 });
        await new Promise(r => setTimeout(r, 3000 * (i + 1)));
      }
    }
    throw lastErr;
  };

  const login = async (username, password) => {
    try {
      const response = await postLoginWithRetry(username, password);
      const { user: userData, needs_role_choice } = response.data;
      applyAuth(response.data);
      if (needs_role_choice) setRoleChooser({ open: true, busy: false });
      toast.success(`Καλώς ήρθατε, ${userData.full_name}!`);
      return true;
    } catch (error) {
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      if (status === 401) {
        toast.error("Λάθος στοιχεία σύνδεσης");
      } else if (!error.response || status >= 500) {
        toast.error("Ο διακομιστής δεν απαντά αυτή τη στιγμή (ξεκινά ή είναι προσωρινά μη διαθέσιμος). Δοκιμάστε ξανά σε λίγο.", { duration: 10000 });
      } else {
        toast.error(typeof detail === "string" ? detail : "Σφάλμα σύνδεσης");
      }
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    toast.info("Αποσυνδεθήκατε επιτυχώς");
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, selectRole, openRoleChooser }}>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <RoleChooser open={roleChooser.open} busy={roleChooser.busy} roles={user?.roles || []} current={user?.role}
                     onSelect={selectRole} onClose={() => setRoleChooser({ open: false, busy: false })} />
        <Routes>
          <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
          <Route path="/forgot-password" element={user ? <Navigate to="/" replace /> : <ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/register" element={user ? <Navigate to="/" replace /> : <RegisterPage />} />
          
          <Route path="/" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            <Route index element={<DefaultPage />} />
            <Route path="today" element={<Today />} />
            <Route path="cases" element={<CasesList />} />
            <Route path="cases/:id" element={<CaseDetail />} />
            <Route path="import" element={<ImportCases />} />
            <Route path="calendar" element={<CalendarView />} />
            <Route path="pretrial" element={<PretrialManagement />} />
            <Route path="reports" element={<Reports />} />
            <Route path="statistics" element={<Statistics />} />
            <Route path="hearings" element={<Hearings />} />
            <Route path="bench-calendar" element={<BenchCalendar />} />
            <Route path="assignment-board" element={<AssignmentBoard />} />
            <Route path="kpold" element={<KpolD />} />
            <Route path="messages" element={<Messages />} />
            <Route path="correspondence" element={<Correspondence />} />
            <Route path="dispatch" element={<Dispatch />} />
            <Route path="printouts" element={<Printouts />} />
            <Route path="judge-cases" element={<JudgeCases />} />
            <Route path="hearing-calculator" element={<HearingCalculator />} />
            <Route path="judge-reviews" element={<JudgeReviews />} />
            <Route path="exports-history" element={<ExportsHistory />} />
            <Route path="filings-search" element={<FilingsSearch />} />
            <Route path="dockets" element={<Dockets />} />
            <Route path="my-compositions" element={<MyCompositions />} />
            <Route path="my-templates" element={<MyTemplates />} />
            <Route path="admin" element={
              <AdminRoute>
                <AdminPanel />
              </AdminRoute>
            } />
          </Route>
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
